import { cloneDeep } from "lodash";
import * as Yup from "yup";
import { isEmpty } from "lodash";
// Helpers
import { payloadBillingDetails } from "./payloaders.js";
// Components
import { Image } from "rebass";
import { Form } from "../../components/donation";
import { CardIcons } from "../../components/donation/cardIcons";
import { StripeComponents } from "../../components/donation/stripeComponent";
import CardImageList from "../../components/donation/cardImageList.jsx";

export const donationOptions = [
	{
		type: "tribute",
		title: "Gift In&nbsp;Honour or In&nbsp;Memory",
		description:
			"Pay tribute to someone special by making an online gift and acknowledging it with a printed card or an e-card.",
		href: { pathname: `/donate/tribute` },
	},
	{
		type: "standard",
		title: "One-Time Gift",
		description: "Make an online gift. It's quick and easy!",
		href: {
			pathname: `/donate/standard`,
			query: { payment_cycle: "one_time" },
		},
	},
	{
		type: "monthly",
		title: "Monthly Gift",
		description:
			"Become a monthly donor online and support the work of Reena Foundation year-round.",
		href: {
			pathname: `/donate/monthly`,
			query: { payment_cycle: "monthly" },
		},
	},
];

export const infoSection = [
	{
		title: "Gifts of Stocks & Securities",
		description:
			"A donation of publicly traded securities is one of the easiest and most effective ways to support Reena Foundation.",
		descriptionLineTwo:
			"Please call the Foundation office at (905) 763-8254 x 3630 or email us at info@reenafoundation.org",
		alignItems: "flex-end",
	},
	{
		title: "Planned Giving",
		description:
			"Options include leaving a bequest to Reena Foundation in your will, or naming Reena Foundation as the beneficiary of your insurance policy.",
		descriptionLineTwo:
			"For more information on deferred giving opportunities, please call the Foundation office at (905) 763-8254 x 3630 or email us at info@reenafoundation.org",
		alignItems: "flex-end",
	},
	{
		title: "Events",
		description:
			"Visit each event page to learn more, and discover how you can help make a difference in the lives of individuals with developmental disabilities.",
		href: "/events/see-all",
		buttonTitle: "See All Events",
	},
];

export const presetConditionHelper = ({ baseField, setFieldValue, values }) => {
	if (
		values["useContactAddress"] &&
		values[`billing${baseField}`] !== values[`contact${baseField}`]
	) {
		setFieldValue(`billing${baseField}`, values[`contact${baseField}`]);
	}
};

export const getValidationSchema = (form) =>
	Yup.object().shape(
		form
			.reduce((arr, sec) => [...arr, ...sec.elements], [])
			.reduce(
				(acc, cur) => ({ ...acc, [cur.name]: cur.validationSchema }),
				{}
			)
	);

export const getInitialValues = ({ formData = [], fetchedValues = [] }) => {
	const initialFormValues = formData
		.reduce((arr, sec) => [...arr, ...sec.elements], [])
		.reduce((acc, cur) => ({ ...acc, [cur.name]: cur.initialValue }), {});

	if (fetchedValues.length) {
		return fetchedValues.reduce(
			(acc, cur) => ({ ...acc, [cur.key]: cur.value }),
			initialFormValues
		);
	}

	return initialFormValues;
};

export const updateForm = ({ form = [], field }) => {
	if (field instanceof Array) {
		let newForm = cloneDeep(form);

		field.forEach((el) => {
			newForm = updateForm({ form: newForm, field: el });
		});

		return newForm;
	}

	if (typeof field === "object") {
		return form.map((section) => ({
			...section,
			elements: section.elements.map((el) =>
				el.name === field.name ? { ...el, ...field } : el
			),
		}));
	}

	return form;
};

export const renderElements = (
	elements,
	{ values, errors, touched, handleChange, handleBlur, setFieldValue }
) =>
	elements.map(
		(
			{
				name,
				type,
				options,
				optional,
				label,
				displayAsThird,
				displayAsHalf,
				displayCondition,
				presetCondition = () => {},
				disabledCondition = () => {},
				...rest
			},
			i
		) => {
			switch (type) {
				case "stripe":
					return <StripeComponents key="stripe" />;
				case "payment":
					return <CardIcons key={`payment-icons-${i}`} />;
				case "digital-card-images":
					return (
						<CardImageList
							key={`e-card-image-list-${i}`}
							label={label}
							name={name}
							images={options.filter(
								(field) =>
									field.type === values["typeOfTribute"]
							)}
							setFieldValue={setFieldValue}
							values={values}
							error={touched[name] && errors[name]}
						/>
					);
				case "image":
					return <Image {...rest} />;
				default:
					return (
						<Form.FormItem
							data-testid={name}
							key={name}
							type={type}
							name={name}
							label={label}
							value={values[name]}
							presetCondition={presetCondition(
								values,
								setFieldValue
							)}
							disabledCondition={disabledCondition(values)}
							error={touched[name] && errors[name]}
							options={options}
							optional={optional}
							onChange={handleChange}
							onBlur={handleBlur}
							setFieldValue={setFieldValue}
							containerWidth={
								displayAsThird
									? ["100%", "100%", "100%", "100%", "33.33%"]
									: displayAsHalf
									? ["100%", "100%", "100%", "100%", "50%"]
									: undefined
							}
							mr={type != "radio" && [0, 0, 4]}
							hide={evaluateDisplayCondition({
								displayCondition,
								values,
							})}
							{...rest}
						/>
					);
			}
		}
	);

const evaluateDisplayCondition = ({ displayCondition, values }) => {
	if (displayCondition instanceof Array) {
		return displayCondition.reduce((acc, condition) => {
			if (acc) {
				return !(values[condition.name] === condition.value);
			}
		}, true);
	}
	return (
		displayCondition &&
		!(values[displayCondition.name] === displayCondition.value)
	);
};

export const generateFundsList = (data = []) =>
	data.map((f) => ({ id: f.id, ...f.value }));

export const findFund = (data = [], val, selector = "code") =>
	data.find((f) => f[selector] === val);

export const generatePaymentDetails = async ({ stripe, elements }) => {
	// Get card ref
	const cardElement = elements.getElement("cardNumber");

	// Get Stripe Token
	const { token, error } = await stripe.createToken(cardElement);

	if (error) {
		console.error("stripe error: ", error);
		return [null, error.message];
	} else {
		return [token, false];
	}
};

// Campaign
export const submitDonationHandler = async ({
	formType,
	submitDonation,
	values,
	actions,
	stripe,
	elements,
	setStripeError,
	recaptchaInstance,
	onSuccess = () => null,
	enhanceDataFunction = async (data) => data,
}) => {
	actions.setSubmitting(true);

	// Check for validation errors or return
	const validate = await actions.validateForm();

	if (!isEmpty(validate)) {
		actions.setSubmitting(false);
		return;
	}

	let recaptchaToken = await recaptchaInstance.execute("invisible");

	if (stripe && elements) {
		const billing_details = payloadBillingDetails(values);
		const [paymentDetails, error] = await generatePaymentDetails({
			stripe,
			elements,
			billing_details,
		});
		if (error) {
			setStripeError(error);
			actions.setSubmitting(false);
		} else {
			// We allow the page to do its own enhancements to the final data
			const mutationInput = await enhanceDataFunction({
				paymentLastFour: paymentDetails.card.last4,
				paymentToken: paymentDetails.id,
				paymentExpiryMonth: paymentDetails.card.exp_month,
				paymentExpiryYear: paymentDetails.card.exp_year,
				// TEMP VALUE
				// image: 1,
				...values,
				recaptchaToken: process.env.NODE_ENV === 'development' ? 'NOT_A_ROBOT' : recaptchaToken,
			});

			const options = {
				variables: {
					input: mutationInput,
				},
			};

			try {
				const result = await submitDonation(options);

				const {
					data: {
						[`${formType}Donation`]: { errors },
					},
				} = result;

				// Check successful donation, redirect to confirmation page
				if (isEmpty(errors)) {
					onSuccess(mutationInput);
					actions.setSubmitting(false);
				}
			} catch (e) {
				console.log(e);
				actions.setSubmitting(false);
			}
		}
	} else {
		actions.setSubmitting(false);
		console.log("Stripe.js hasn't loaded yet....");
	}
};
